import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { Chip, FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem, Select, Switch, Typography } from "@material-ui/core";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import moment from "moment"
import { AuthContext } from "../../context/Auth/AuthContext";
import { filterConnectionsByUserPermission } from "../../utils/userPermissions";
import { isArray, capitalize } from "lodash";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import AttachFile from "@material-ui/icons/AttachFile";
import { head } from "lodash";
import ConfirmationModal from "../ConfirmationModal";
import MessageVariablesPicker from "../MessageVariablesPicker";
import useQueues from "../../hooks/useQueues";
import UserStatusIcon from "../UserModal/statusIcon";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	// multFieldLine: {
	// 	display: "flex",
	// 	"& > *:not(:last-child)": {
	// 		marginRight: theme.spacing(1),
	// 	},
	// },

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	// formControl: {
	// 	margin: theme.spacing(1),
	// 	minWidth: 120,
	// },
}));

const getScheduleSchema = () =>
	Yup.object().shape({
		body: Yup.string()
			.min(5, i18n.t("scheduleModal.validation.bodyMin"))
			.required(i18n.t("scheduleModal.validation.required")),
		contactId: Yup.number().required(i18n.t("scheduleModal.validation.required")),
		sendAt: Yup.string().required(i18n.t("scheduleModal.validation.required")),
	});

const SCHEDULE_STATUS_LABELS = {
	PENDENTE: "schedules.calendarStatus.agendado",
	AGENDADA: "schedules.calendarStatus.agendado",
	ENVIADA: "schedules.calendarStatus.enviado",
	ERRO: "schedules.calendarStatus.erro",
};

const getScheduleDialogTitle = (status) => {
	if (status === "ERRO") {
		return i18n.t("scheduleModal.titleStatus.sendError");
	}
	const labelKey = SCHEDULE_STATUS_LABELS[status];
	const label = labelKey ? i18n.t(labelKey) : capitalize(status || "");
	return `${i18n.t("scheduleModal.titleStatus.message")} ${label}`;
};

const getDefaultSendAt = () => moment().format("YYYY-MM-DDTHH:mm");

const RECURRENCE_PRESETS = [
	{ key: "1h", intervalo: 4, valorIntervalo: 60 },
	{ key: "6h", intervalo: 4, valorIntervalo: 360 },
	{ key: "12h", intervalo: 4, valorIntervalo: 720 },
	{ key: "1d", intervalo: 1, valorIntervalo: 1 },
	{ key: "2d", intervalo: 1, valorIntervalo: 2 },
	{ key: "3d", intervalo: 1, valorIntervalo: 3 },
	{ key: "1w", intervalo: 2, valorIntervalo: 1 },
	{ key: "2w", intervalo: 2, valorIntervalo: 2 },
	{ key: "1m", intervalo: 3, valorIntervalo: 1 },
];

const findRecurrencePresetKey = (intervalo, valorIntervalo) =>
	RECURRENCE_PRESETS.find(
		(preset) =>
			preset.intervalo === Number(intervalo) &&
			preset.valorIntervalo === Number(valorIntervalo)
	)?.key || "1d";

const ScheduleModal = ({
	open,
	onClose,
	scheduleId,
	contactId,
	cleanContact,
	reload,
	initialMessage,
	presetWhatsappId,
	presetQueueId,
	presetChannel,
}) => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const isMounted = useRef(true);
	const { companyId } = user;

	const initialState = {
		body: "",
		contactId: "",
		sendAt: getDefaultSendAt(),
		sentAt: "",
		openTicket: "enabled",
		ticketUserId: "",
		queueId: "",
		statusTicket: "open",
		intervalo: 1,
		valorIntervalo: 0,
		enviarQuantasVezes: 1,
		tipoDias: 4,
		assinar: false
	};

	const initialContact = {
		id: "",
		name: "",
		channel: "",
		whatsappId: null
	}

	const mapContactOption = (contact) => ({
		id: contact.id,
		name: contact.name,
		channel: contact.channel || "whatsapp",
		whatsappId: contact.whatsappId || null
	});

	const getConnectionById = (connectionId, connections = whatsapps) =>
		connections.find((item) => Number(item.id) === Number(connectionId));

	const fetchContactsForConnection = async (connection) => {
		const params = { companyId };
		if (connection?.channel && connection.channel !== "whatsapp") {
			params.channel = connection.channel;
			params.whatsappId = connection.id;
		} else if (connection?.channel === "whatsapp") {
			params.channel = "whatsapp";
		}

		const { data: contactList } = await api.get("/contacts/list", { params });
		const customList = (contactList || []).map(mapContactOption);
		setContacts([initialContact, ...customList]);
	};

	const [schedule, setSchedule] = useState(initialState);
	const [currentContact, setCurrentContact] = useState(initialContact);
	const [contacts, setContacts] = useState([initialContact]);
	const [recurrenceEnabled, setRecurrenceEnabled] = useState(false);
	const [recurrencePreset, setRecurrencePreset] = useState("1d");
	const [attachment, setAttachment] = useState(null);
	const attachmentFile = useRef(null);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [deleteScheduleConfirmOpen, setDeleteScheduleConfirmOpen] = useState(false);
	const messageInputRef = useRef();
	const [channelFilter, setChannelFilter] = useState("");
	const [whatsapps, setWhatsapps] = useState([]);
	const [selectedWhatsapps, setSelectedWhatsapps] = useState("");
	const [loading, setLoading] = useState(false);
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState(null);
	const { findAll: findAllQueues } = useQueues();
	const [options, setOptions] = useState([]);
	const [searchParam, setSearchParam] = useState("");

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (isMounted.current) {
			const loadQueues = async () => {
				const list = await findAllQueues();
				setAllQueues(list);
				setQueues(list);
			};
			loadQueues();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (searchParam.length < 3) {
			setLoading(false);
			setSelectedQueue("");
			return;
		}
		const delayDebounceFn = setTimeout(() => {
			setLoading(true);
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/");
					setOptions(data.users);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchUsers();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam]);

	useEffect(() => {
		if (!open) return;

		const fetchConnections = async () => {
			try {
				let data = [];

				if (presetWhatsappId || presetChannel) {
					const { data: filtered } = await api.get("/whatsapp/filter", {
						params: { session: 0, channel: presetChannel || channelFilter },
					});
					data = filtered || [];
				} else {
					const { data: all } = await api.get("/whatsapp", { params: { session: 0 } });
					data = filterConnectionsByUserPermission(
						(all || []).filter((item) => item.status === "CONNECTED"),
						user
					);
				}

				const mappedWhatsapps = data.map((whatsapp) => ({
					...whatsapp,
					selected: false,
				}));

				setWhatsapps(mappedWhatsapps);

				if (presetWhatsappId) {
					const presetConnection = getConnectionById(presetWhatsappId, mappedWhatsapps);
					setSelectedWhatsapps(presetWhatsappId);
					setChannelFilter(presetConnection?.channel || presetChannel || "whatsapp");
					await fetchContactsForConnection(presetConnection);
					return;
				}

				if (mappedWhatsapps.length === 1) {
					const onlyConnection = mappedWhatsapps[0];
					setSelectedWhatsapps(onlyConnection.id);
					setChannelFilter(onlyConnection.channel || "whatsapp");
					await fetchContactsForConnection(onlyConnection);
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchConnections();
	}, [open, presetWhatsappId, presetChannel, companyId]);

	useEffect(() => {
		if (contactId && contacts.length) {
			const contact = contacts.find(c => c.id === contactId);
			if (contact) {
				setCurrentContact(contact);
			}
		}
	}, [contactId, contacts]);

	useEffect(() => {
		if (!open || scheduleId) return;

		setSchedule((prevState) => ({
			...prevState,
			sendAt: getDefaultSendAt(),
			...(initialMessage ? { body: initialMessage } : {}),
			...(contactId ? { contactId } : {}),
		}));

		if (presetWhatsappId) {
			setSelectedWhatsapps(presetWhatsappId);
		}

		if (presetQueueId) {
			setSelectedQueue(presetQueueId);
		}

		if (presetChannel) {
			setChannelFilter(presetChannel);
		}
	}, [open, scheduleId, initialMessage, contactId, presetWhatsappId, presetQueueId, presetChannel]);

	useEffect(() => {
		if (open) {
			try {
				(async () => {
					if (!scheduleId && !presetWhatsappId && !selectedWhatsapps) {
						await fetchContactsForConnection(null);
					}

					if (contactId) {
						setSchedule(prevState => {
							return { ...prevState, contactId }
						});
					}

					if (!scheduleId) return;

					const { data } = await api.get(`/schedules/${scheduleId}`);
					setSchedule(prevState => {
						return { ...prevState, ...data, sendAt: moment(data.sendAt).format('YYYY-MM-DDTHH:mm') };
					});
					console.log(data)
					if (data.whatsapp) {
						setSelectedWhatsapps(data.whatsapp.id);
						setChannelFilter(data.whatsapp.channel || "whatsapp");
						await fetchContactsForConnection(data.whatsapp);
					}

					if (data.ticketUser) {
						setSelectedUser(data.ticketUser);
					}
					if (data.queueId) {
						setSelectedQueue(data.queueId);
					}

					if (Number(data.valorIntervalo) > 0) {
						setRecurrenceEnabled(true);
						setRecurrencePreset(
							findRecurrencePresetKey(data.intervalo, data.valorIntervalo)
						);
					} else {
						setRecurrenceEnabled(false);
						setRecurrencePreset("1d");
					}

					setCurrentContact(data.contact);
				})()
			} catch (err) {
				toastError(err);
			}
		}
	}, [scheduleId, contactId, open, user, presetWhatsappId]);

	const filterOptions = createFilterOptions({
		trim: true,
	});

	const handleClose = () => {
		onClose();
		setAttachment(null);
		setSchedule({ ...initialState, sendAt: getDefaultSendAt() });
		setSelectedUser(null);
		setSelectedQueue(null);
		setSelectedWhatsapps("");
		setChannelFilter("");
		setContacts([initialContact]);
		setRecurrenceEnabled(false);
		setRecurrencePreset("1d");
	};

	const handleAttachmentFile = (e) => {
		const file = head(e.target.files);
		if (file) {
			setAttachment(file);
		}
	};

	const IconChannel = (channel) => {
		switch (channel) {
			case "facebook":
				return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
			case "instagram":
				return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
			case "whatsapp":
				return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
			default:
				return "error";
		}
	};

	const selectedConnection = getConnectionById(selectedWhatsapps);

	const contactMatchesConnection = (contact, connection) => {
		if (!contact?.id) return true;
		if (!connection) {
			return !channelFilter || contact.channel === channelFilter;
		}

		const connectionChannel = connection.channel || "whatsapp";
		const contactChannel = contact.channel || "whatsapp";

		if (Number(contact.whatsappId) === Number(connection.id)) {
			return true;
		}

		if (connectionChannel === "whatsapp") {
			return contactChannel === "whatsapp";
		}

		return contactChannel === connectionChannel;
	};

	const filteredContacts = contacts.filter((contact) =>
		contactMatchesConnection(contact, selectedConnection)
	);

	const handleConnectionChange = async (connectionId) => {
		setSelectedWhatsapps(connectionId);
		const connection = getConnectionById(connectionId);
		if (connection?.channel) {
			setChannelFilter(connection.channel);
		}

		if (
			currentContact?.id &&
			connection &&
			!contactMatchesConnection(currentContact, connection)
		) {
			setCurrentContact(initialContact);
			setSchedule((prevState) => ({ ...prevState, contactId: "" }));
		}

		try {
			await fetchContactsForConnection(connection);
		} catch (err) {
			toastError(err);
		}
	};

	const renderOption = option => {
		if (option.name) {
			return <>
				{IconChannel(option.channel || "whatsapp")}
				<Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
					{option.name}
				</Typography>
			</>
		} else {
			return `${i18n.t("newTicketModal.add")} ${option.name}`;
		}
	};
	const handleSaveSchedule = async values => {
		const preset = RECURRENCE_PRESETS.find((item) => item.key === recurrencePreset)
			|| RECURRENCE_PRESETS.find((item) => item.key === "1d");

		const scheduleData = {
			...values,
			userId: user.id,
			whatsappId: selectedWhatsapps || presetWhatsappId || null,
			ticketUserId: selectedUser?.id || null,
			queueId: selectedQueue || presetQueueId || null,
			openTicket: "enabled",
			statusTicket: "open",
			intervalo: recurrenceEnabled ? preset.intervalo : 1,
			valorIntervalo: recurrenceEnabled ? preset.valorIntervalo : 0,
			enviarQuantasVezes: recurrenceEnabled
				? Math.max(2, Number(values.enviarQuantasVezes) || 2)
				: 1,
			tipoDias: 4,
		};

		try {
			if (scheduleId) {
				await api.put(`/schedules/${scheduleId}`, scheduleData);
				if (attachment != null) {
					const formData = new FormData();
					formData.append("file", attachment);
					await api.post(
						`/schedules/${scheduleId}/media-upload`,
						formData
					);
				}
			} else {
				const { data } = await api.post("/schedules", scheduleData);
				if (attachment != null) {
					const formData = new FormData();
					formData.append("file", attachment);
					await api.post(`/schedules/${data.id}/media-upload`, formData);
				}
			}
			toast.success(i18n.t("scheduleModal.success"));
			if (typeof reload == 'function') {
				reload();
			}
			if (contactId) {
				if (typeof cleanContact === 'function') {
					cleanContact();
					history.push('/schedules');
				}
			}
		} catch (err) {
			toastError(err);
		}
		setCurrentContact(initialContact);
		setSchedule(initialState);
		handleClose();
	};
	const handleClickMsgVar = async (msgVar, setValueFunc) => {
		const el = messageInputRef.current;
		const firstHalfText = el.value.substring(0, el.selectionStart);
		const secondHalfText = el.value.substring(el.selectionEnd);
		const newCursorPos = el.selectionStart + msgVar.length;

		setValueFunc("body", `${firstHalfText}${msgVar}${secondHalfText}`);

		await new Promise(r => setTimeout(r, 100));
		messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
	};

	const deleteMedia = async () => {
		if (attachment) {
			setAttachment(null);
			attachmentFile.current.value = null;
		}

		if (schedule.mediaPath) {
			await api.delete(`/schedules/${schedule.id}/media-upload`);
			setSchedule((prev) => ({
				...prev,
				mediaPath: null,
			}));
			toast.success(i18n.t("scheduleModal.toasts.deleted"));
			if (typeof reload == "function") {
				reload();
			}
		}
	};

	const handleDeleteSchedule = async () => {
		if (!scheduleId) return;

		try {
			await api.delete(`/schedules/${scheduleId}`);
			toast.success(i18n.t("schedules.toasts.deleted"));
			if (typeof reload === "function") {
				reload();
			}
			setDeleteScheduleConfirmOpen(false);
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<ConfirmationModal
				title={i18n.t("scheduleModal.confirmationModal.deleteMediaTitle")}
				open={confirmationOpen}
				onClose={() => setConfirmationOpen(false)}
				onConfirm={deleteMedia}
			>
				{i18n.t("scheduleModal.confirmationModal.deleteMediaMessage")}
			</ConfirmationModal>
			<ConfirmationModal
				title={i18n.t("scheduleModal.confirmationModal.deleteTitle")}
				open={deleteScheduleConfirmOpen}
				onClose={() => setDeleteScheduleConfirmOpen(false)}
				onConfirm={handleDeleteSchedule}
			>
				{i18n.t("scheduleModal.confirmationModal.deleteMessage")}
			</ConfirmationModal>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{getScheduleDialogTitle(schedule.status)}
				</DialogTitle>
				<div style={{ display: "none" }}>
					<input
						type="file"
						accept=".png,.jpg,.jpeg"
						ref={attachmentFile}
						onChange={(e) => handleAttachmentFile(e)}
					/>
				</div>
				<Formik
					initialValues={schedule}
					enableReinitialize={true}
					validationSchema={getScheduleSchema()}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveSchedule(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<Grid container spacing={1}>
									{!presetWhatsappId && (
										<Grid item xs={12}>
											<FormControl
												variant="outlined"
												margin="dense"
												fullWidth
												className={classes.formControl}
											>
												<InputLabel id="whatsapp-selection-label">
													{i18n.t("scheduleModal.form.connection")}
												</InputLabel>
												<Field
													as={Select}
													label={i18n.t("scheduleModal.form.connection")}
													placeholder={i18n.t("scheduleModal.form.connection")}
													labelId="whatsapp-selection-label"
													id="whatsappIds"
													name="whatsappIds"
													required
													error={touched.whatsappId && Boolean(errors.whatsappId)}
													value={selectedWhatsapps}
													onChange={(event) => handleConnectionChange(event.target.value)}
												>
													{whatsapps &&
														whatsapps.map((whatsapp) => (
															<MenuItem key={whatsapp.id} value={whatsapp.id}>
																<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
																	{IconChannel(whatsapp.channel || "whatsapp")}
																	{whatsapp.name}
																</span>
															</MenuItem>
														))}
												</Field>
											</FormControl>
										</Grid>
									)}
									{!contactId && (
										<Grid item xs={12}>
											<FormControl variant="outlined" fullWidth>
												<Autocomplete
													fullWidth
													disabled={!presetWhatsappId && whatsapps.length > 1 && !selectedWhatsapps}
													value={currentContact}
													options={filteredContacts}
													onChange={(e, contact) => {
														const selectedContactId = contact ? contact.id : '';
														setSchedule({ ...schedule, contactId: selectedContactId });
														setCurrentContact(contact ? contact : initialContact);
														if (contact?.whatsappId) {
															setSelectedWhatsapps(contact.whatsappId);
															const connection = getConnectionById(contact.whatsappId);
															if (connection?.channel) {
																setChannelFilter(connection.channel);
															}
														} else if (contact?.channel) {
															setChannelFilter(contact.channel);
															const matchingConnections = whatsapps.filter(
																(item) => item.channel === contact.channel
															);
															if (matchingConnections.length === 1) {
																setSelectedWhatsapps(matchingConnections[0].id);
															}
														}
													}}
													getOptionLabel={(option) => option.name || ""}
													renderOption={renderOption}
													getOptionSelected={(option, value) => value.id === option.id}
													renderInput={(params) => (
														<TextField
															{...params}
															variant="outlined"
															placeholder={i18n.t("scheduleModal.form.contact")}
															helperText={
																!presetWhatsappId && whatsapps.length > 1 && !selectedWhatsapps
																	? i18n.t("scheduleModal.form.selectConnectionFirst")
																	: ""
															}
														/>
													)}
												/>
											</FormControl>
										</Grid>
									)}
								</Grid>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										rows={9}
										multiline={true}
										label={i18n.t("scheduleModal.form.body")}
										name="body"
										inputRef={messageInputRef}
										error={touched.body && Boolean(errors.body)}
										helperText={touched.body && errors.body}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
								</div>
								<Grid item xs={12} md={12} xl={6}>
									<MessageVariablesPicker
										disabled={isSubmitting}
										onClick={value => handleClickMsgVar(value, setFieldValue)}
									/>
								</Grid>
								<Grid container spacing={1}>
									<Grid item xs={12} md={12} xl={12}>
										<Field
											as={TextField}
											label={i18n.t("scheduleModal.form.sendAt")}
											type="datetime-local"
											name="sendAt"
											InputLabelProps={{ shrink: true }}
											error={touched.sendAt && Boolean(errors.sendAt)}
											helperText={touched.sendAt && errors.sendAt}
											variant="outlined"
											fullWidth
											size="small"
											style={{ marginTop: "8px" }}
										/>
									</Grid>
									<Grid item xs={12} md={12} xl={12}>
										<FormControlLabel
											control={
												<Field
													as={Switch}
													color="primary"
													name="assinar"
													checked={values.assinar}
												/>
											}
											label={i18n.t("scheduleModal.form.assinar")}
										/>
									</Grid>
								</Grid>
								<Grid container spacing={1} style={{ marginTop: 8 }}>
									<Grid item xs={12}>
										<FormControlLabel
											control={
												<Switch
													color="primary"
													checked={recurrenceEnabled}
													onChange={(e) => {
														const enabled = e.target.checked;
														setRecurrenceEnabled(enabled);
														if (enabled && Number(values.enviarQuantasVezes) < 2) {
															setFieldValue("enviarQuantasVezes", 3);
														}
													}}
												/>
											}
											label={i18n.t("schedules.modal.repeatEnabled")}
										/>
										<Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
											{i18n.t("schedules.modal.repeatDescription")}
										</Typography>
									</Grid>
									{recurrenceEnabled && (
										<>
											<Grid item xs={12} md={6}>
												<FormControl size="small" fullWidth variant="outlined">
													<InputLabel id="schedule-repeat-every-label">
														{i18n.t("schedules.modal.repeatEvery")}
													</InputLabel>
													<Select
														labelId="schedule-repeat-every-label"
														value={recurrencePreset}
														onChange={(e) => setRecurrencePreset(e.target.value)}
														label={i18n.t("schedules.modal.repeatEvery")}
													>
														{RECURRENCE_PRESETS.map((preset) => (
															<MenuItem key={preset.key} value={preset.key}>
																{i18n.t(`schedules.modal.repeatPresets.${preset.key}`)}
															</MenuItem>
														))}
													</Select>
												</FormControl>
											</Grid>
											<Grid item xs={12} md={6}>
												<Field
													as={TextField}
													type="number"
													inputProps={{ min: 2, max: 99 }}
													label={i18n.t("schedules.modal.repeatTimes")}
													name="enviarQuantasVezes"
													size="small"
													error={
														touched.enviarQuantasVezes &&
														Boolean(errors.enviarQuantasVezes)
													}
													variant="outlined"
													fullWidth
												/>
											</Grid>
										</>
									)}
								</Grid>
								{(schedule.mediaPath || attachment) && (
									<Grid xs={12} item>
										<Button startIcon={<AttachFile />}>
											{attachment ? attachment.name : schedule.mediaName}
										</Button>
										<IconButton
											onClick={() => setConfirmationOpen(true)}
											color="secondary"
										>
											<DeleteOutline color="secondary" />
										</IconButton>
									</Grid>
								)}
							</DialogContent>
							<DialogActions>
								{scheduleId && (schedule.sentAt === null || schedule.sentAt === "") && (
									<Button
										color="secondary"
										onClick={() => setDeleteScheduleConfirmOpen(true)}
										disabled={isSubmitting}
										startIcon={<DeleteOutline />}
										style={{ marginRight: "auto" }}
									>
										{i18n.t("scheduleModal.buttons.delete")}
									</Button>
								)}
								{!attachment && !schedule.mediaPath && (
									<Button
										color="primary"
										onClick={() => attachmentFile.current.click()}
										disabled={isSubmitting}
										variant="outlined"
									>
										{i18n.t("quickMessages.buttons.attach")}
									</Button>
								)}
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("scheduleModal.buttons.cancel")}
								</Button>
								{(schedule.sentAt === null || schedule.sentAt === "") && (
									<Button
										type="submit"
										color="primary"
										disabled={isSubmitting}
										variant="contained"
										className={classes.btnWrapper}
									>
										{scheduleId
											? `${i18n.t("scheduleModal.buttons.okEdit")}`
											: `${i18n.t("scheduleModal.buttons.okAdd")}`}
										{isSubmitting && (
											<CircularProgress
												size={24}
												className={classes.buttonProgress}
											/>
										)}
									</Button>
								)}
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ScheduleModal;